// Shift assignment from the employee form — three fields that ride on the
// existing employee create / update endpoints (`shift_id`,
// `shift_effective_from`, `weekly_off_days`). Mirrors the pattern in
// employeeProfileFields.js and is shared by the Society Portal pages and the
// Society Controller modal.
//
// Sending them needs `shift.assign` on top of employee.create / employee.update,
// so the whole section is hidden without it.
//
// The backend works out which writes to make from the single shift you send:
// assigning where there was none, closing the old assignment the day before and
// opening a new one where it differs, updating the offs where only those
// changed, or ending it where shift_id is null. A future-dated assignment is
// the one case it refuses — that returns 409 ASSIGNMENT_OVERLAP and belongs on
// the assignments screen.

import { sameDays } from './shiftConstants';

// Form-state keys. weekly_off_days is a number[]; the other two are strings,
// with '' meaning "no shift selected".
export const SHIFT_FIELD_KEYS = ['shift_id', 'shift_effective_from', 'weekly_off_days'];

export const emptyShift = {
    shift_id: '',
    shift_effective_from: '',
    weekly_off_days: [],
};

// Form state from an employee record. Every single-employee response — GET
// /:id, POST and PUT alike — carries `shift_assignment`: the assignment in
// force TODAY, or null. (The list endpoints do not.)
export const shiftFromEmployee = (emp) => {
    const a = emp?.shift_assignment;
    if (!a) return { ...emptyShift };
    return {
        shift_id: a.shift_id != null ? String(a.shift_id) : '',
        // Deliberately not pre-filled from effective_from: this box means "when
        // the CHANGE takes effect", and it only appears once the shift actually
        // changes. The stored start date is shown as a caption instead.
        shift_effective_from: '',
        weekly_off_days: Array.isArray(a.weekly_off_days) ? a.weekly_off_days : [],
    };
};

/**
 * The baseline the form is compared against, kept OUTSIDE form state — it is
 * what was stored, not what the user is editing. Holding it separately also
 * keeps `effective_from` (needed only for the "since 1 Oct" caption) out of the
 * form, where it would leak into the employee payload as an unknown key.
 *
 * null when the employee has no shift, which reads the same as an empty form.
 */
export const shiftBaseline = (emp) => {
    const a = emp?.shift_assignment;
    if (!a) return { shift_id: '', weekly_off_days: [], effective_from: null };
    return {
        shift_id: a.shift_id != null ? String(a.shift_id) : '',
        weekly_off_days: Array.isArray(a.weekly_off_days) ? a.weekly_off_days : [],
        effective_from: a.effective_from || null,
    };
};

export const validateShift = (values) => {
    const e = {};
    // All seven days off is rejected server-side — that is not an assignment.
    if ((values.weekly_off_days || []).length >= 7) {
        e.weekly_off_days = 'At least one working day is required';
    }
    // Weekly offs without a shift have nothing to attach to.
    if (!values.shift_id && (values.weekly_off_days || []).length > 0) {
        e.shift_id = 'Select a shift, or clear the weekly offs';
    }
    return e;
};

/**
 * Request-body slice for the shift fields — the one place the omit-vs-null
 * distinction is decided.
 *
 *   { shift_id: 12 }    assign / change to shift 12
 *   { shift_id: null }  CLEAR the employee's shift
 *   (key absent)        leave the shift exactly as it was
 *
 * So an unchanged form must send NOTHING. A form whose "nothing selected" state
 * is null would otherwise wipe the shift on every unrelated employee edit.
 *
 * `original` is the shift slice the form was seeded with (from
 * shiftFromEmployee). On create there is nothing to compare against, so pass
 * undefined and anything selected is sent.
 */
export const buildShiftPayload = (values, original) => {
    const selected = values.shift_id ? Number(values.shift_id) : null;
    const offs = values.weekly_off_days || [];

    if (!original) {
        // Create: nothing chosen means the employee simply has no shift yet.
        if (selected == null) return {};
        return {
            shift_id: selected,
            ...(values.shift_effective_from ? { shift_effective_from: values.shift_effective_from } : {}),
            weekly_off_days: offs,
        };
    }

    const originalShift = original.shift_id ? Number(original.shift_id) : null;
    const shiftChanged = selected !== originalShift;
    const offsChanged = !sameDays(offs, original.weekly_off_days);
    if (!shiftChanged && !offsChanged) return {};

    return {
        // null here is the deliberate "clear it" signal, reached only because
        // something actually changed.
        shift_id: selected,
        ...(values.shift_effective_from ? { shift_effective_from: values.shift_effective_from } : {}),
        weekly_off_days: selected == null ? null : offs,
    };
};

// Split a flat form into { shift, rest } so shift keys never leak into the base
// payload uncontrolled — buildShiftPayload decides what actually gets sent.
export const splitShift = (form) => {
    const shift = {};
    const rest = {};
    Object.entries(form).forEach(([k, v]) => {
        if (SHIFT_FIELD_KEYS.includes(k)) shift[k] = v;
        else rest[k] = v;
    });
    return { shift, rest };
};

// PUT /api/employees/:id is NOT transactional: if the shift half fails the
// employee is still saved, and the message says so. Treating that as "the save
// failed" is wrong — the employee row on screen is now stale, not unchanged.
// (POST is transactional; there the employee is rolled back too.)
export const isPartialShiftFailure = (message) =>
    typeof message === 'string' && message.startsWith('Employee updated');
